import React, { useState, useEffect } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { CheckCircle, AlertTriangle, Loader } from 'lucide-react';

// --- PDF.js Worker Configuration (fixed) ---
// Uses the worker from unpkg, automatically matching pdfjs version
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

// --- SIMULATED API CALL ---
const fetchTemplatesApi = () =>
  new Promise((resolve) =>
    setTimeout(
      () =>
        resolve({
          success: true,
          templates: [
            {
              id: 'template_1',
              name: 'Proposal 1',
              url: 'https://hlfppt-proposals.s3.ap-south-1.amazonaws.com/sample_template_1.pdf',
            },
            {
              id: 'template_2',
              name: 'Proposal 2',
              url: 'https://hlfppt-proposals.s3.ap-south-1.amazonaws.com/sample_template_2.pdf',
            },
            {
              id: 'template_3',
              name: 'Proposal 3',
              url: 'https://hlfppt-proposals.s3.ap-south-1.amazonaws.com/sample_template_3.pdf',
            },
          ],
        }),
      1000
    )
  );

// --- Reusable Template Card Component ---
const TemplateCard = ({ template, isSelected, onSelect }) => {
  return (
    <label
      onClick={() => onSelect(template.id)}
      className={`relative group block cursor-pointer border-2 p-2 rounded-xl transition-all ${
        isSelected ? 'border-fuchsia-600 shadow-lg' : 'border-slate-200 hover:border-slate-400'
      }`}
    >
      <input
        type="radio"
        name="pdf-template"
        value={template.id}
        checked={isSelected}
        onChange={() => onSelect(template.id)}
        className="sr-only"
      />

      <div className="w-full h-48 bg-slate-50 flex items-center justify-center rounded-md overflow-hidden">
        <Document
          file={template.url}
          loading={
            <div className="flex flex-col items-center text-slate-500 text-xs">
              <Loader className="animate-spin mb-2" size={18} />
              <span>Loading preview…</span>
            </div>
          }
          error={
            <div className="flex flex-col items-center text-red-500 p-2 text-center text-xs">
              <AlertTriangle className="mb-2" size={18} />
              <span>Failed to load</span>
            </div>
          }
          // Helpful logging if anything goes wrong
          onLoadError={(err) => {
            console.error(`PDF load error for template ${template.id}:`, err);
          }}
          onSourceError={(err) => {
            console.error(`PDF source error for template ${template.id}:`, err);
          }}
        >
          <Page
            pageNumber={1}
            width={160}
            renderTextLayer={false}
            renderAnnotationLayer={false}
          />
        </Document>
      </div>

      <p className="font-semibold text-sm text-center mt-2 text-slate-700">
        {template.name}
      </p>

      {isSelected && (
        <div className="absolute top-2 right-2 bg-violet-500 text-white rounded-full p-1">
          <CheckCircle size={16} />
        </div>
      )}
    </label>
  );
};

const ChooseTemplateStep = ({
  onTemplateSelect,
  selectedTemplate,
  onTemplatesLoaded,
  onLoadingError,
  disabled,
}) => {
  const [pdfTemplates, setPdfTemplates] = useState([]);
  const [temporarySelection, setTemporarySelection] = useState(null);
  const [isSaved, setIsSaved] = useState(false);

  // Fetch templates & initialize selection
  useEffect(() => {
    if (disabled) return;

    const fetchAndInitialize = async () => {
      try {
        // 1. Fetch templates from the API first
        const res = await fetchTemplatesApi();
        if (!res.success || !res.templates) {
          throw new Error('Could not fetch templates.');
        }
        setPdfTemplates(res.templates);
        onTemplatesLoaded && onTemplatesLoaded();

        // 2. Determine the initial selection from localStorage with clear priority
        let initialTemplateId = null;

        // Source A: Session-specific choice (highest priority)
        const sessionTemplateId = localStorage.getItem('selectedTemplateId');

        // Source B: User profile default (fallback)
        const userDataString = localStorage.getItem('user_data');

        if (sessionTemplateId) {
          initialTemplateId = sessionTemplateId;
        } else if (userDataString) {
          try {
            const userData = JSON.parse(userDataString);
            const profileTemplateId = userData?.user_info?.proposal_template;

            // Ensure the value from storage is a valid, non-empty string
            if (profileTemplateId && typeof profileTemplateId === 'string') {
              initialTemplateId = profileTemplateId.trim();
            }
          } catch (error) {
            console.error('Failed to parse user_data:', error);
          }
        }

        // 3. Apply the selection ONLY if it's valid
        if (initialTemplateId) {
          const templateExists = res.templates.some(
            (t) => t.id === initialTemplateId
          );

          if (templateExists) {
            setTemporarySelection(initialTemplateId);
            onTemplateSelect(initialTemplateId);
          }
        }
      } catch (error) {
        console.error('Template fetch error:', error);
        onLoadingError && onLoadingError(error.message);
      }
    };

    fetchAndInitialize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disabled]);

 const TemplateSkeleton = () => (
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-4">
    {[1, 2, 3].map((i) => (
      <div
        key={i}
        className="rounded-2xl border bg-white p-3 shadow-sm"
      >
        <div
          className="h-56 rounded-xl bg-gray-200 relative overflow-hidden
          before:absolute before:inset-0
          before:-translate-x-full
          before:animate-[shimmer_1.5s_infinite]
          before:bg-gradient-to-r
          before:from-transparent before:via-white/60 before:to-transparent"
        >
          <div className="absolute top-3 left-3 right-3 h-4 bg-gray-300 rounded" />
        </div>

        <div
          className="mt-4 h-4 w-24 mx-auto rounded bg-gray-300 relative overflow-hidden
          before:absolute before:inset-0
          before:-translate-x-full
          before:animate-[shimmer_1.5s_infinite]
          before:bg-gradient-to-r
          before:from-transparent before:via-white/60 before:to-transparent"
        />
      </div>
    ))}
  </div>
);



  const handleSaveSelection = () => {
    if (temporarySelection) {
      localStorage.setItem('selectedTemplateId', temporarySelection);
      onTemplateSelect(temporarySelection);
      setIsSaved(true);
      setTimeout(() => setIsSaved(false), 2000);
    }
  };

  if (disabled) {
    return (
      <div className="text-center p-4 bg-slate-50 rounded-lg text-sm text-slate-500">
        Complete Step 1 to proceed.
      </div>
    );
  }

  if (pdfTemplates.length === 0) {
    return (
      <div className="text-center p-4 bg-slate-50 rounded-lg text-sm text-slate-500">
        <TemplateSkeleton/>
      </div>
    );
  }

  const hasUnsavedChanges =
    temporarySelection && temporarySelection !== selectedTemplate;

    

  return (
    <div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {pdfTemplates.map((template) => (
          <TemplateCard
            key={template.id}
            template={template}
            isSelected={temporarySelection === template.id}
            onSelect={setTemporarySelection}
          />
        ))}
      </div>

      <div className="mt-6 text-center">
        <button
          onClick={handleSaveSelection}
          disabled={!hasUnsavedChanges || isSaved}
          className={`px-8 py-3 rounded-lg font-semibold text-white transition-all shadow-md ${
            isSaved
              ? 'bg-green-600'
              : hasUnsavedChanges
              ? 'bg-gradient-to-r from-indigo-600 to-fuchsia-600 hover:bg-fuchsia-700'
              : 'bg-slate-400 cursor-not-allowed'
          }`}
        >
          {isSaved ? (
            <span className="flex items-center gap-2">
              <CheckCircle size={20} /> Template Saved!
            </span>
          ) : (
            'Save Selection'
          )}
        </button>
      </div>
    </div>
  );
};

export default ChooseTemplateStep;